import {
	IGetGamesShort,
	ICreateGame,
	IEditGame,
	IGetPaginatedGames,
	IUpdateGamePositions,
	IAssignGames,
	ICreateGameCategory,
	IUpdateGameCategory,
	IGetPaginatedGameCategories,
	ICreateGameDeveloper,
	IUpdateGameDeveloper,
	IGetPaginatedGameDevelopers,
} from 'types';
import {
	serviceGetGameInfo,
	serviceGetGamesShortInfo,
	serviceCreateGame,
	serviceEditGame,
	serviceGetPaginatedGames,
	serviceDeleteGame,
	serviceGetGamePositionList,
	serviceGetGamePositionChains,
	serviceUpdateGamePositions,
	serviceGetCustomersForAssignGame,
	serviceGetRestaurantsByIDsCustomersForGames,
	serviceGetGamesByIDsRestaurantsForGames,
	serviceAssignGames,
	serviceGetGameCategoryInfo,
	serviceGetGameCategoriesShortInfo,
	serviceGetGameCategoriesShortInfoByRestaurantIDs,
	serviceCreateGameCategory,
	serviceUpdateGameCategory,
	serviceGetPaginatedGameCategories,
	serviceDeleteGameCategory,
	serviceGetGameDeveloperInfo,
	serviceGetGameDevelopersShortInfo,
	serviceCreateGameDeveloper,
	serviceUpdateGameDeveloper,
	serviceGetPaginatedGameDevelopers,
	serviceDeleteGameDeveloper,
} from 'services/api/games';
import { createAppErrorMessage } from 'utils/helpers';

const getGameInfo = async (gameID: string) => {
	try {
		const response = await serviceGetGameInfo(gameID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getGamesShortInfo = async (data: IGetGamesShort) => {
	try {
		const response = await serviceGetGamesShortInfo(data);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createGame = async (data: ICreateGame) => {
	try {
		await serviceCreateGame(data);
	} catch (err) {
		throw err;
	}
};

const editGame = async (data: IEditGame) => {
	try {
		await serviceEditGame(data);
	} catch (err) {
		throw err;
	}
};

const getPaginatedGames = async (args: IGetPaginatedGames) => {
	try {
		const response = await serviceGetPaginatedGames(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteGame = async (gameID: string) => {
	try {
		await serviceDeleteGame(gameID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingGame');
		throw new Error(errorMessage);
	}
};

const getGamePositionList = async (restaurantID: string) => {
	try {
		const response = await serviceGetGamePositionList(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getGamePositionChains = async (restaurantID: string) => {
	try {
		const response = await serviceGetGamePositionChains(restaurantID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const updateGamePositions = async (data: IUpdateGamePositions) => {
	try {
		await serviceUpdateGamePositions(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingGamePositions');
		throw new Error(errorMessage);
	}
};

const getCustomersForAssignGame = async () => {
	try {
		const response = await serviceGetCustomersForAssignGame();
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsByIDsCustomersForGames = async (customerIDs: string[]) => {
	try {
		const response = await serviceGetRestaurantsByIDsCustomersForGames(customerIDs);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const getRestaurantsByIDsRestaurantsForGames = async (restaurantIDs: string[]) => {
	try {
		const response = await serviceGetGamesByIDsRestaurantsForGames(restaurantIDs);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const assignGames = async (data: IAssignGames[]) => {
	try {
		await serviceAssignGames(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorAssignGame');
		throw new Error(errorMessage);
	}
};

// GameCategories
const getGameCategoryInfo = async (categoryID: string) => {
	try {
		const response = await serviceGetGameCategoryInfo(categoryID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getGameCategoriesShortInfo = async () => {
	try {
		const response = await serviceGetGameCategoriesShortInfo();
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const getGameCategoriesShortInfoByRestaurantIDs = async (restaurantIds: string[]) => {
	try {
		const response = await serviceGetGameCategoriesShortInfoByRestaurantIDs(restaurantIds);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createGameCategory = async (data: ICreateGameCategory) => {
	try {
		await serviceCreateGameCategory(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingGameCategory');
		throw new Error(errorMessage);
	}
};

const updateGameCategory = async (data: IUpdateGameCategory) => {
	try {
		await serviceUpdateGameCategory(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingGameCategory');
		throw new Error(errorMessage);
	}
};

const getPaginatedGameCategories = async (args: IGetPaginatedGameCategories) => {
	try {
		const response = await serviceGetPaginatedGameCategories(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteGameCategory = async (categoryID: string) => {
	try {
		await serviceDeleteGameCategory(categoryID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingGameCategory');
		throw new Error(errorMessage);
	}
};
// GameCategories

// GameDevelopers
const getGameDeveloperInfo = async (developerID: string) => {
	try {
		const response = await serviceGetGameDeveloperInfo(developerID);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const getGameDevelopersShortInfo = async () => {
	try {
		const response = await serviceGetGameDevelopersShortInfo();
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataInListsNotPulled');
		throw new Error(errorMessage);
	}
};

const createGameDeveloper = async (data: ICreateGameDeveloper) => {
	try {
		await serviceCreateGameDeveloper(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorCreatingGameDeveloper');
		throw new Error(errorMessage);
	}
};

const updateGameDeveloper = async (data: IUpdateGameDeveloper) => {
	try {
		await serviceUpdateGameDeveloper(data);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorUpdatingGameDeveloper');
		throw new Error(errorMessage);
	}
};

const getPaginatedGameDevelopers = async (args: IGetPaginatedGameDevelopers) => {
	try {
		const response = await serviceGetPaginatedGameDevelopers(args);
		return response;
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'dataNotPulled');
		throw new Error(errorMessage);
	}
};

const deleteGameDeveloper = async (developerID: string) => {
	try {
		await serviceDeleteGameDeveloper(developerID);
	} catch (err) {
		const errorMessage = createAppErrorMessage(err, 'errorDeletingGameDeveloperr');
		throw new Error(errorMessage);
	}
};
// GameDevelopers

export {
	getGameInfo,
	getGamesShortInfo,
	createGame,
	editGame,
	getPaginatedGames,
	deleteGame,
	getGamePositionList,
	getGamePositionChains,
	updateGamePositions,
	getCustomersForAssignGame,
	getRestaurantsByIDsCustomersForGames,
	getRestaurantsByIDsRestaurantsForGames,
	assignGames,
	getGameCategoryInfo,
	getGameCategoriesShortInfo,
	getGameCategoriesShortInfoByRestaurantIDs,
	createGameCategory,
	updateGameCategory,
	getPaginatedGameCategories,
	deleteGameCategory,
	getGameDeveloperInfo,
	getGameDevelopersShortInfo,
	createGameDeveloper,
	updateGameDeveloper,
	getPaginatedGameDevelopers,
	deleteGameDeveloper,
};
